/**
 * Notification sound utility using Web Audio API.
 *
 * Synthesizes short tones — no audio files needed.
 * Respects browser autoplay policy by resuming AudioContext on first interaction.
 */

import { useUIStore } from '../stores';

class NotificationSound {
  private ctx: AudioContext | null = null;
  private resumed = false;

  private getContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext();
    }
    // Resume suspended context (browser autoplay policy)
    if (!this.resumed && this.ctx.state === 'suspended') {
      this.ctx.resume();
      this.resumed = true;
    }
    return this.ctx;
  }

  private playTone(frequency: number, duration: number, volume = 0.3): void {
    const ctx = this.getContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.frequency.value = frequency;
    osc.type = 'sine';
    gain.gain.value = volume;

    // Fade out to avoid click
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration);
  }

  /** Short blip for incoming messages */
  playMessage(): void {
    if (!useUIStore.getState().soundEnabled) return;
    this.playTone(800, 0.1, 0.25);
  }

  /** Rising two-note chime for new conversations */
  playConversation(): void {
    if (!useUIStore.getState().soundEnabled) return;
    this.playTone(600, 0.12, 0.25);
    setTimeout(() => this.playTone(800, 0.15, 0.25), 130);
  }

  /** Two quick pulses for urgent / escalation */
  playUrgent(): void {
    if (!useUIStore.getState().soundEnabled) return;
    this.playTone(900, 0.1, 0.35);
    setTimeout(() => this.playTone(900, 0.1, 0.35), 150);
  }
}

export const notificationSound = new NotificationSound();
